import { useCallback, useEffect, useState } from "react";
import { Checkbox, SkeletonText, useToast } from "@astrum/ui";
import { api, ApiError } from "../lib/api.js";
import "./workspace-settings.css";

type Missing = { id: string; email: string; displayName: string; role: string };

/**
 * "Require two-factor authentication", with the list of people it would catch.
 *
 * Saves on toggle rather than through the settings form's Save button: it is a
 * policy switch, and the list beneath it should reflect the setting as soon as
 * it flips. People without two-factor can still sign in; they are held on the
 * setup screen until they enrol.
 */
export function TwoFactorPolicy() {
  const { toast } = useToast();
  const [required, setRequired] = useState<boolean | null>(null);
  const [saving, setSaving] = useState(false);
  const [missing, setMissing] = useState<Missing[]>([]);
  const [hasMore, setHasMore] = useState(false);

  const loadMissing = useCallback(() => {
    api
      .get<{ users: Missing[]; hasMore: boolean }>("/admin/two-factor/missing?limit=100")
      .then((res) => {
        setMissing(res.users);
        setHasMore(res.hasMore);
      })
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    api
      .get<{ settings: { requireTwoFactor: boolean } }>("/admin/settings")
      .then((res) => setRequired(res.settings.requireTwoFactor))
      .catch(() => toast("Could not load the two-factor setting.", { tone: "danger" }));
    loadMissing();
  }, [loadMissing, toast]);

  const toggle = useCallback(
    (next: boolean) => {
      setSaving(true);
      api
        .put<{ settings: { requireTwoFactor: boolean } }>("/admin/settings", { requireTwoFactor: next })
        .then((res) => {
          setRequired(res.settings.requireTwoFactor);
          toast(next ? "Two-factor is now required." : "Two-factor is no longer required.", { tone: "ok" });
          loadMissing();
        })
        .catch((error: unknown) => {
          toast(error instanceof ApiError ? error.message : "Could not change the setting.", { tone: "danger" });
        })
        .finally(() => setSaving(false));
    },
    [loadMissing, toast],
  );

  if (required === null) {
    return (
      <div className="wsettings">
        <SkeletonText lines={3} />
      </div>
    );
  }

  return (
    <div className="wsettings">
      <section className="wsettings__section">
        <div className="wsettings__section-head">
          <h3>Two-factor authentication</h3>
          <p>
            When required, people without two-factor can still sign in, but are held on a setup screen until they
            enrol.
          </p>
        </div>
        <Checkbox
          label="Require two-factor authentication"
          checked={required}
          disabled={saving}
          onChange={(e) => toggle(e.target.checked)}
        />
        <div>
          <strong>Not enrolled yet ({missing.length}{hasMore ? "+" : ""})</strong>
          {missing.length === 0 ? (
            <p className="wsettings__hint">Everyone active has two-factor on.</p>
          ) : (
            <ul className="wsettings__hint">
              {missing.map((person) => (
                <li key={person.id}>
                  {person.displayName} · {person.email}
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>
    </div>
  );
}
